import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import {
    FiArrowLeft, FiEdit2, FiAward, FiUsers,
    FiCheckCircle, FiXCircle, FiAlertCircle, FiTrendingUp,
    FiCheckSquare, FiLock, FiUnlock, FiClock, FiCheck, FiShield,
} from "react-icons/fi";
import { useApp } from "../../context/AppContext";
import examService from "../../services/examService";

const EMPTY_SUMMARY = {
    total: 0,
    passed: 0,
    failed: 0,
    absent: 0,
    avgPct: 0,
    highest: 0,
    lowest: 0,
    approvedCount: 0,
    pendingCount: 0,
    onHoldCount: 0,
    gradeDist: {},
};

const normalizeResultReport = (payload) => {
    const exam = payload?.exam && !Array.isArray(payload.exam)
        ? { ...payload.exam, subjects: Array.isArray(payload.exam.subjects) ? payload.exam.subjects : [] }
        : null;

    return {
        exam,
        results: Array.isArray(payload?.data) ? payload.data : [],
        summary: {
            ...EMPTY_SUMMARY,
            ...(payload?.summary && !Array.isArray(payload.summary) ? payload.summary : {}),
            gradeDist: payload?.summary?.gradeDist && !Array.isArray(payload.summary.gradeDist)
                ? payload.summary.gradeDist
                : {},
        },
    };
};

const GRADE_COLORS = {
    O: { bg: "#dcfce7", color: "#15803d" },
    "A+": { bg: "#e8f1f8", color: "#002747" },
    A: { bg: "#e8f1f8", color: "#003b67" },
    "B+": { bg: "#fef9c3", color: "#92400e" },
    B: { bg: "#fef3c7", color: "#d97706" },
    F: { bg: "#fee2e2", color: "#dc2626" },
    Ab: { bg: "#f3f4f6", color: "#6b7280" },
    "–": { bg: "#f3f4f6", color: "#6b7280" },
};

const APPROVAL_STYLES = {
    Approved: { bg: "#dcfce7", color: "#15803d", border: "#86efac", icon: FiCheck, label: "Approved" },
    Pending: { bg: "#fef9c3", color: "#a16207", border: "#fde047", icon: FiClock, label: "Pending" },
    "On Hold": { bg: "#fee2e2", color: "#b91c1c", border: "#fca5a5", icon: FiLock, label: "On Hold" },
};

function StatCard({ label, value, icon: Icon, color }) {
    return (
        <div className="card" style={{ padding: "1rem 1.25rem", borderLeft: `4px solid ${color}` }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 6 }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: `${color}18`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Icon size={14} color={color} />
                </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 700, color }}>{value}</div>
            <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 3 }}>{label}</div>
        </div>
    );
}

export default function ResultReport() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useApp();
    const isAdmin = ["admin", "superadmin"].includes(user?.role);

    const [report, setReport] = useState(null);
    const [loading, setLoading] = useState(true);
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");

    const [showPublishModal, setShowPublishModal] = useState(false);
    const [showUnpublishModal, setShowUnpublishModal] = useState(false);
    const [autoApproveRemaining, setAutoApproveRemaining] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);
    const [rowLoading, setRowLoading] = useState({});

    const fetchReport = useCallback(() => {
        setLoading(true);
        examService.getResultsByExam(id)
            .then((res) => {
                const normalized = normalizeResultReport(res);
                if (!normalized.exam) throw new Error("Result report could not be loaded.");
                setReport(normalized);
            })
            .catch((err) => {
                toast.error(err.message || "Failed to load report");
                navigate("/exams");
            })
            .finally(() => setLoading(false));
    }, [id, navigate]);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleToggleApproval = async (studentId, currentStatus) => {
        if (!isAdmin) return;
        const nextStatus = currentStatus === "Approved" ? "Pending" : "Approved";
        setRowLoading((prev) => ({ ...prev, [studentId]: true }));
        try {
            await examService.updateStudentApproval(id, studentId, nextStatus);
            setReport((prev) => {
                if (!prev) return prev;
                const updatedResults = prev.results.map((r) =>
                    String(r.student?._id) === String(studentId)
                        ? { ...r, approvalStatus: nextStatus }
                        : r
                );
                const approvedCount = updatedResults.filter((r) => r.approvalStatus === "Approved").length;
                const pendingCount = updatedResults.filter((r) => !r.approvalStatus || r.approvalStatus === "Pending").length;
                const onHoldCount = updatedResults.filter((r) => r.approvalStatus === "On Hold").length;
                return {
                    ...prev,
                    results: updatedResults,
                    summary: { ...prev.summary, approvedCount, pendingCount, onHoldCount },
                };
            });
            toast.success(`Result marked as ${nextStatus}`);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to update status");
        } finally {
            setRowLoading((prev) => ({ ...prev, [studentId]: false }));
        }
    };

    const handleSetStudentStatus = async (studentId, statusValue) => {
        if (!isAdmin) return;
        setRowLoading((prev) => ({ ...prev, [studentId]: true }));
        try {
            await examService.updateStudentApproval(id, studentId, statusValue);
            setReport((prev) => {
                if (!prev) return prev;
                const updatedResults = prev.results.map((r) =>
                    String(r.student?._id) === String(studentId)
                        ? { ...r, approvalStatus: statusValue }
                        : r
                );
                const approvedCount = updatedResults.filter((r) => r.approvalStatus === "Approved").length;
                const pendingCount = updatedResults.filter((r) => !r.approvalStatus || r.approvalStatus === "Pending").length;
                const onHoldCount = updatedResults.filter((r) => r.approvalStatus === "On Hold").length;
                return {
                    ...prev,
                    results: updatedResults,
                    summary: { ...prev.summary, approvedCount, pendingCount, onHoldCount },
                };
            });
            toast.success(`Result marked as ${statusValue}`);
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Failed to update status");
        } finally {
            setRowLoading((prev) => ({ ...prev, [studentId]: false }));
        }
    };

    const handleApproveAll = async () => {
        if (!isAdmin) return;
        setActionLoading(true);
        try {
            const res = await examService.bulkUpdateApproval(id, { approveAll: true, approvalStatus: "Approved" });
            toast.success(res.message || "All student results approved.");
            fetchReport();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Bulk approval failed");
        } finally {
            setActionLoading(false);
        }
    };

    const handlePublish = async () => {
        setActionLoading(true);
        try {
            const res = await examService.updatePublishStatus(id, "Results Published", autoApproveRemaining);
            toast.success(res.message || "Results published successfully.");
            setShowPublishModal(false);
            fetchReport();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Publishing failed");
        } finally {
            setActionLoading(false);
        }
    };

    const handleUnpublish = async () => {
        setActionLoading(true);
        try {
            const res = await examService.updatePublishStatus(id, "Active");
            toast.success(res.message || "Results unpublished.");
            setShowUnpublishModal(false);
            fetchReport();
        } catch (err) {
            toast.error(err.response?.data?.message || err.message || "Unpublishing failed");
        } finally {
            setActionLoading(false);
        }
    };

    if (loading) return <div className="loading-center"><div className="spinner" />Loading results...</div>;
    if (!report) return null;

    const { exam, summary, results } = report;
    const subjectList = Array.isArray(exam.subjects) ? exam.subjects : [];
    const isPublished = exam.status === "Results Published";

    const filtered = results.filter((r) => {
        const matchesSearch =
            !search.trim() ||
            r.student?.fullName?.toLowerCase().includes(search.toLowerCase()) ||
            r.student?.enrollmentNo?.toLowerCase().includes(search.toLowerCase()) ||
            r.student?.rollNo?.toLowerCase().includes(search.toLowerCase());

        const currentStatus = r.approvalStatus || "Pending";
        const matchesStatus = statusFilter === "All" || currentStatus === statusFilter;

        return matchesSearch && matchesStatus;
    });

    const passRate = summary.total > 0 ? Math.round((summary.passed / summary.total) * 100) : 0;
    const approvedRate = summary.total > 0 ? Math.round(((summary.approvedCount || 0) / summary.total) * 100) : 0;

    return (
        <div>
            <div className="page-header" style={{ flexWrap: "wrap", gap: "0.75rem", alignItems: "center" }}>
                <div>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.6rem", flexWrap: "wrap" }}>
                        <h1 className="page-title" style={{ margin: 0 }}>{exam.title} — Results</h1>
                        {isPublished ? (
                            <span style={{ background: "#dcfce7", color: "#15803d", border: "1px solid #86efac", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <FiCheckCircle size={13} /> Published to Students
                            </span>
                        ) : (
                            <span style={{ background: "#fef9c3", color: "#92400e", border: "1px solid #fde047", padding: "3px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 4 }}>
                                <FiClock size={13} /> Pending Admin Approval
                            </span>
                        )}
                    </div>
                    <p className="page-subtitle" style={{ marginTop: 4 }}>
                        {exam.courseAdmitted} · {exam.courseYear} · {exam.examType}
                        {exam.examDate ? ` · ${exam.examDate}` : ""}
                    </p>
                </div>

                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", alignItems: "center" }}>
                    <button className="btn btn-outline" onClick={() => navigate("/exams")}>
                        <FiArrowLeft size={14} /> Back
                    </button>
                    <button className="btn btn-outline" onClick={() => navigate(`/exams/${id}/results/entry`)}>
                        <FiEdit2 size={14} /> Edit Marks
                    </button>

                    {isAdmin && (
                        <>
                            {isPublished ? (
                                <button
                                    className="btn btn-outline"
                                    onClick={() => setShowUnpublishModal(true)}
                                    style={{ borderColor: "#fca5a5", color: "#dc2626", display: "inline-flex", alignItems: "center", gap: "0.4rem" }}
                                    title="Unpublish results to hide them from student dashboard"
                                >
                                    <FiLock size={14} /> Unpublish Results
                                </button>
                            ) : (
                                <button
                                    className="btn btn-primary"
                                    onClick={() => setShowPublishModal(true)}
                                    style={{ background: "#16a34a", borderColor: "#16a34a", display: "inline-flex", alignItems: "center", gap: "0.4rem", fontWeight: 700 }}
                                >
                                    <FiCheckCircle size={14} /> Approve & Publish Results
                                </button>
                            )}
                        </>
                    )}
                </div>
            </div>

            {isAdmin && (
                <div className="card" style={{ marginBottom: "1.25rem", padding: "1.1rem 1.3rem", background: isPublished ? "#f8fafc" : "#fffbeb", border: isPublished ? "1px solid var(--border)" : "1px solid #fef08a" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.9rem" }}>
                            <div style={{ width: 42, height: 42, borderRadius: 10, background: isPublished ? "#dcfce7" : "#fef3c7", display: "flex", alignItems: "center", justifyContent: "center", color: isPublished ? "#15803d" : "#b45309", flexShrink: 0 }}>
                                <FiShield size={20} />
                            </div>
                            <div>
                                <div style={{ fontWeight: 700, fontSize: 14 }}>
                                    Student Result Approvals: <span style={{ color: "#16a34a" }}>{summary.approvedCount || 0}</span> / {summary.total} Approved ({approvedRate}%)
                                </div>
                                <div style={{ fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>
                                    {summary.pendingCount > 0 ? `${summary.pendingCount} student(s) pending review · ` : "All students reviewed · "}
                                    {isPublished
                                        ? "Results are live on student and parent dashboards."
                                        : "Results are currently hidden from students until published."}
                                </div>
                            </div>
                        </div>

                        <div style={{ display: "flex", gap: "0.5rem", alignItems: "center", flexWrap: "wrap" }}>
                            <button
                                type="button"
                                className="btn btn-outline btn-sm"
                                onClick={handleApproveAll}
                                disabled={actionLoading || summary.approvedCount === summary.total}
                                style={{ display: "inline-flex", alignItems: "center", gap: "0.35rem", fontWeight: 600 }}
                            >
                                <FiCheckSquare size={13} /> {actionLoading ? "Updating..." : "Approve All Students"}
                            </button>

                            {!isPublished && (
                                <button
                                    type="button"
                                    className="btn btn-primary btn-sm"
                                    onClick={() => setShowPublishModal(true)}
                                    style={{ background: "#16a34a", borderColor: "#16a34a", display: "inline-flex", alignItems: "center", gap: "0.35rem", fontWeight: 700 }}
                                >
                                    <FiCheckCircle size={13} /> Publish Results →
                                </button>
                            )}
                        </div>
                    </div>

                    <div style={{ marginTop: "0.85rem" }}>
                        <div style={{ height: 8, borderRadius: 99, background: "#e2e8f0", overflow: "hidden", display: "flex" }}>
                            <div
                                style={{
                                    height: "100%",
                                    width: `${approvedRate}%`,
                                    background: "#16a34a",
                                    transition: "width 0.4s ease",
                                }}
                                title={`${summary.approvedCount} Approved`}
                            />
                            {summary.onHoldCount > 0 && (
                                <div
                                    style={{
                                        height: "100%",
                                        width: `${(summary.onHoldCount / summary.total) * 100}%`,
                                        background: "#dc2626",
                                    }}
                                    title={`${summary.onHoldCount} On Hold`}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}

            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))", gap: "1rem", marginBottom: "1.25rem" }}>
                <StatCard label="Students Appeared" value={summary.total} icon={FiUsers} color="#003b67" />
                <StatCard label="Passed" value={summary.passed} icon={FiCheckCircle} color="#16a34a" />
                <StatCard label="Failed" value={summary.failed} icon={FiXCircle} color="#dc2626" />
                <StatCard label="Absent" value={summary.absent} icon={FiAlertCircle} color="#6b7280" />
                <StatCard label="Class Average" value={`${summary.avgPct}%`} icon={FiTrendingUp} color="#d97706" />
                <StatCard label="Pass Rate" value={`${passRate}%`} icon={FiAward} color="#7c3aed" />
            </div>

            <div className="card" style={{ marginBottom: "1.25rem" }}>
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between" }}>
                    <span>Performance Overview — {exam.title}</span>
                    <span style={{ fontSize: 12, fontWeight: 400, color: "var(--text-muted)" }}>
                        Highest: <strong style={{ color: "#16a34a" }}>{summary.highest}%</strong> · Lowest: <strong style={{ color: "#dc2626" }}>{summary.lowest}%</strong>
                    </span>
                </div>
                <div className="card-body">
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: 13, marginBottom: 8 }}>
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>{summary.passed} Passed</span>
                        <span style={{ color: "#dc2626", fontWeight: 600 }}>{summary.failed} Failed · {summary.absent} Absent</span>
                    </div>
                    <div style={{ height: 12, borderRadius: 99, background: "#fee2e2", overflow: "hidden" }}>
                        <div style={{ height: "100%", width: `${passRate}%`, background: "linear-gradient(90deg,#16a34a,#22d3ee)", borderRadius: 99, transition: "width 0.7s" }} />
                    </div>
                    <div style={{ fontSize: 11, color: "var(--text-muted)", marginTop: 5, display: "flex", justifyContent: "space-between" }}>
                        <span style={{ color: "#16a34a", fontWeight: 600 }}>{passRate}% passed</span>
                        <span style={{ color: "#dc2626", fontWeight: 600 }}>{100 - passRate}% failed/absent</span>
                    </div>

                    {Object.keys(summary.gradeDist).length > 0 && (
                        <div style={{ marginTop: "1rem" }}>
                            <div style={{ fontSize: 12, fontWeight: 600, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em", marginBottom: "0.5rem" }}>
                                Grade Distribution
                            </div>
                            <div style={{ display: "flex", flexWrap: "wrap", gap: "0.4rem" }}>
                                {Object.entries(summary.gradeDist).sort(([a], [b]) => a.localeCompare(b)).map(([grade, count]) => {
                                    const gc = GRADE_COLORS[grade] || GRADE_COLORS["–"];
                                    return (
                                        <span key={grade} style={{ background: gc.bg, color: gc.color, padding: "3px 12px", borderRadius: 99, fontSize: 12.5, fontWeight: 700 }}>
                                            {grade}: {count}
                                        </span>
                                    );
                                })}
                            </div>
                        </div>
                    )}
                </div>
            </div>

            <div className="card">
                <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "0.75rem" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                        <span style={{ fontWeight: 700 }}>Student Results ({filtered.length})</span>
                        <div style={{ display: "inline-flex", background: "var(--bg-secondary, #f1f5f9)", padding: "2px", borderRadius: 8, marginLeft: 8 }}>
                            {["All", "Approved", "Pending", "On Hold"].map((st) => (
                                <button
                                    key={st}
                                    type="button"
                                    onClick={() => setStatusFilter(st)}
                                    style={{
                                        border: "none",
                                        background: statusFilter === st ? "#fff" : "transparent",
                                        color: statusFilter === st ? "var(--text)" : "var(--text-muted)",
                                        fontWeight: statusFilter === st ? 700 : 500,
                                        fontSize: 12,
                                        padding: "3px 9px",
                                        borderRadius: 6,
                                        cursor: "pointer",
                                        boxShadow: statusFilter === st ? "0 1px 3px rgba(0,0,0,0.08)" : "none",
                                    }}
                                >
                                    {st}
                                    {st === "Approved" && ` (${summary.approvedCount || 0})`}
                                    {st === "Pending" && ` (${summary.pendingCount || 0})`}
                                    {st === "On Hold" && summary.onHoldCount > 0 ? ` (${summary.onHoldCount})` : ""}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className="search-bar" style={{ width: 240 }}>
                        <input
                            placeholder="Search by name, roll, enrollment..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                        />
                    </div>
                </div>

                {results.length === 0 ? (
                    <div className="card-body" style={{ textAlign: "center", color: "var(--text-muted)", padding: "2rem" }}>
                        No results entered yet. Go to <strong>Entry</strong> to add marks.
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table>
                            <thead>
                                <tr>
                                    <th>Rank</th>
                                    <th>Student</th>
                                    {subjectList.map((sub) => (
                                        <th key={sub.name} style={{ textAlign: "center" }}>
                                            <div>{sub.name}</div>
                                            <div style={{ fontWeight: 400, fontSize: 10 }}>/{sub.maxMarks}</div>
                                        </th>
                                    ))}
                                    <th style={{ textAlign: "center" }}>Total</th>
                                    <th style={{ textAlign: "center" }}>%</th>
                                    <th style={{ textAlign: "center" }}>Grade</th>
                                    <th style={{ textAlign: "center" }}>Result</th>
                                    <th style={{ textAlign: "center", minWidth: 120 }}>Admin Approval</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered.map((r, i) => {
                                    const gc = GRADE_COLORS[r.grade] || GRADE_COLORS["–"];
                                    const appStatus = r.approvalStatus || "Pending";
                                    const as = APPROVAL_STYLES[appStatus] || APPROVAL_STYLES.Pending;
                                    const isRowBusy = rowLoading[r.student?._id];

                                    return (
                                        <tr key={r._id}>
                                            <td style={{ fontSize: 12, color: "var(--text-muted)", textAlign: "center" }}>
                                                {r.result === "Pass" ? (
                                                    <span style={{ fontWeight: 700, color: "#002747" }}>{i + 1}</span>
                                                ) : "—"}
                                            </td>
                                            <td>
                                                <div style={{ fontWeight: 600, fontSize: 13 }}>{r.student?.fullName}</div>
                                                <div style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
                                                    {r.student?.enrollmentNo}
                                                    {r.student?.rollNo ? ` · Roll: ${r.student.rollNo}` : ""}
                                                </div>
                                            </td>
                                            {subjectList.map((sub) => {
                                                const m = r.marks?.find((mk) => mk.subjectName === sub.name);
                                                const isFail = m && !m.isAbsent && m.marksObtained < m.passingMarks;
                                                return (
                                                    <td key={sub.name} style={{ textAlign: "center", fontSize: 13 }}>
                                                        {m?.isAbsent ? (
                                                            <span style={{ color: "#6b7280", fontWeight: 600 }}>AB</span>
                                                        ) : (
                                                            <span style={{ color: isFail ? "#dc2626" : "var(--text)", fontWeight: isFail ? 700 : 400 }}>
                                                                {m?.marksObtained ?? "—"}
                                                            </span>
                                                        )}
                                                    </td>
                                                );
                                            })}
                                            <td style={{ textAlign: "center", fontWeight: 700 }}>
                                                {r.result === "Absent" ? "—" : `${r.totalObtained}/${r.totalMaxMarks}`}
                                            </td>
                                            <td style={{ textAlign: "center", fontSize: 13 }}>
                                                {r.result === "Absent" ? "—" : `${r.percentage}%`}
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <span style={{ background: gc.bg, color: gc.color, padding: "2px 10px", borderRadius: 99, fontSize: 12, fontWeight: 700 }}>
                                                    {r.grade}
                                                </span>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4, fontSize: 12.5, fontWeight: 600 }}>
                                                    {r.result === "Pass" && <><FiCheckCircle size={12} color="#16a34a" /> <span style={{ color: "#16a34a" }}>Pass</span></>}
                                                    {r.result === "Fail" && <><FiXCircle size={12} color="#dc2626" /> <span style={{ color: "#dc2626" }}>Fail</span></>}
                                                    {r.result === "Absent" && <><FiAlertCircle size={12} color="#6b7280" /> <span style={{ color: "#6b7280" }}>Absent</span></>}
                                                </div>
                                            </td>
                                            <td style={{ textAlign: "center" }}>
                                                {isAdmin ? (
                                                    <div style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                                                        <button
                                                            type="button"
                                                            disabled={isRowBusy}
                                                            onClick={() => handleToggleApproval(r.student?._id, appStatus)}
                                                            style={{
                                                                background: as.bg,
                                                                color: as.color,
                                                                border: `1px solid ${as.border}`,
                                                                padding: "3px 8px",
                                                                borderRadius: 99,
                                                                fontSize: 11.5,
                                                                fontWeight: 700,
                                                                cursor: "pointer",
                                                                display: "inline-flex",
                                                                alignItems: "center",
                                                                gap: 4,
                                                                transition: "all 0.15s ease",
                                                            }}
                                                            title="Click to toggle between Approved / Pending"
                                                        >
                                                            <as.icon size={11} />
                                                            {isRowBusy ? "..." : as.label}
                                                        </button>

                                                        {appStatus !== "On Hold" ? (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSetStudentStatus(r.student?._id, "On Hold")}
                                                                style={{
                                                                    background: "transparent",
                                                                    border: "none",
                                                                    color: "var(--text-muted)",
                                                                    fontSize: 10,
                                                                    cursor: "pointer",
                                                                    padding: "2px 4px",
                                                                }}
                                                                title="Put on hold"
                                                            >
                                                                Hold
                                                            </button>
                                                        ) : (
                                                            <button
                                                                type="button"
                                                                onClick={() => handleSetStudentStatus(r.student?._id, "Approved")}
                                                                style={{
                                                                    background: "transparent",
                                                                    border: "none",
                                                                    color: "#16a34a",
                                                                    fontSize: 10,
                                                                    fontWeight: 700,
                                                                    cursor: "pointer",
                                                                    padding: "2px 4px",
                                                                }}
                                                                title="Approve"
                                                            >
                                                                Approve
                                                            </button>
                                                        )}
                                                    </div>
                                                ) : (
                                                    <span style={{ background: as.bg, color: as.color, border: `1px solid ${as.border}`, padding: "2px 8px", borderRadius: 99, fontSize: 11.5, fontWeight: 700, display: "inline-flex", alignItems: "center", gap: 3 }}>
                                                        <as.icon size={11} /> {as.label}
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {showPublishModal && (
                <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                    <div className="card" style={{ maxWidth: 480, width: "100%", padding: "1.5rem", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#dcfce7", color: "#15803d", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <FiCheckCircle size={22} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Approve & Publish Results</h3>
                                <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{exam.title}</p>
                            </div>
                        </div>

                        <div style={{ background: "#f8fafc", borderRadius: 8, padding: "0.85rem 1rem", marginBottom: "1rem", fontSize: 13, border: "1px solid var(--border)" }}>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ color: "var(--text-muted)" }}>Total Students:</span>
                                <span style={{ fontWeight: 700 }}>{summary.total}</span>
                            </div>
                            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                <span style={{ color: "#16a34a" }}>Approved Students:</span>
                                <span style={{ fontWeight: 700, color: "#16a34a" }}>{summary.approvedCount || 0}</span>
                            </div>
                            {summary.pendingCount > 0 && (
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 4 }}>
                                    <span style={{ color: "#b45309" }}>Pending Approval:</span>
                                    <span style={{ fontWeight: 700, color: "#b45309" }}>{summary.pendingCount}</span>
                                </div>
                            )}
                            {summary.onHoldCount > 0 && (
                                <div style={{ display: "flex", justifyContent: "space-between" }}>
                                    <span style={{ color: "#dc2626" }}>On Hold:</span>
                                    <span style={{ fontWeight: 700, color: "#dc2626" }}>{summary.onHoldCount}</span>
                                </div>
                            )}
                        </div>

                        {summary.pendingCount > 0 && (
                            <label style={{ display: "flex", alignItems: "flex-start", gap: 8, fontSize: 13, cursor: "pointer", marginBottom: "1.25rem", userSelect: "none" }}>
                                <input
                                    type="checkbox"
                                    checked={autoApproveRemaining}
                                    onChange={(e) => setAutoApproveRemaining(e.target.checked)}
                                    style={{ marginTop: 2 }}
                                />
                                <span>
                                    Automatically mark all <strong>{summary.pendingCount} pending student(s)</strong> as Approved upon publishing.
                                </span>
                            </label>
                        )}

                        <p style={{ fontSize: 13, color: "var(--text-muted)", marginBottom: "1.25rem" }}>
                            Once published, approved students will immediately be able to view their scores, grades, and marksheet in their student/parent portal.
                        </p>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => setShowPublishModal(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handlePublish}
                                disabled={actionLoading}
                                style={{ background: "#16a34a", borderColor: "#16a34a", fontWeight: 700 }}
                            >
                                {actionLoading ? "Publishing..." : "Yes, Publish Results"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {showUnpublishModal && (
                <div className="modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", padding: "1rem" }}>
                    <div className="card" style={{ maxWidth: 440, width: "100%", padding: "1.5rem", boxShadow: "0 20px 25px -5px rgba(0,0,0,0.2)" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: "0.75rem", marginBottom: "1rem" }}>
                            <div style={{ width: 44, height: 44, borderRadius: 12, background: "#fee2e2", color: "#dc2626", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                                <FiUnlock size={22} />
                            </div>
                            <div>
                                <h3 style={{ margin: 0, fontSize: 17, fontWeight: 700 }}>Unpublish Results?</h3>
                                <p style={{ margin: 0, fontSize: 12.5, color: "var(--text-muted)", marginTop: 2 }}>{exam.title}</p>
                            </div>
                        </div>

                        <p style={{ fontSize: 13, color: "var(--text)", lineHeight: 1.5, marginBottom: "1.25rem" }}>
                            Unpublishing will set the exam status back to <strong>Active</strong> and immediately hide results from the student and parent dashboards. You can re-publish at any time.
                        </p>

                        <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.5rem" }}>
                            <button
                                type="button"
                                className="btn btn-outline"
                                onClick={() => setShowUnpublishModal(false)}
                                disabled={actionLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleUnpublish}
                                disabled={actionLoading}
                                style={{ background: "#dc2626", borderColor: "#dc2626", fontWeight: 700 }}
                            >
                                {actionLoading ? "Unpublishing..." : "Unpublish Results"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}
